import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import {useDispatch, useSelector} from 'react-redux';
import {useFormInput} from '../../utils/utilities';
import CustomButton from '../CustomButton';
import Select from '../Select';
import {addGarage, editGarage, openModal} from '../../actions';
import {getEntrancesByBuildingApi} from '../../api/entrances.service';
import {fieldError, isFieldIvalid} from '../../utils/errorHandler';

/**
 * Add / edit a garage inside a project. A garage can be linked to one, many or no entrances
 * of that project. Entrances are fetched directly (not through redux) so the Entrances tab's
 * paginated list is left untouched.
 */
const GarageModal = ({edit}) => {
  const dispatch = useDispatch();
  const {modalProps} = useSelector(s => s.modal);
  const {garagesLoading, garagesError} = useSelector(s => s.garages);
  const garage = edit ? modalProps.garage : null;
  const buildingId = modalProps.buildingID;
  const name = useFormInput(garage?.name || '');
  const [entranceIds, setEntranceIds] = useState(garage?.entranceIds || []);
  const [entrances, setEntrances] = useState([]);
  const [entrancesLoading, setEntrancesLoading] = useState(false);

  useEffect(() => {
    if (!buildingId) return;
    let cancelled = false;
    setEntrancesLoading(true);
    getEntrancesByBuildingApi(buildingId, {page: 1, size: 500})
      .then(res => {
        if (!cancelled) setEntrances(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setEntrances([]);
      })
      .finally(() => {
        if (!cancelled) setEntrancesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId]);

  const onSubmit = e => {
    e.preventDefault();
    if (edit) {
      dispatch(editGarage(name.value, buildingId, entranceIds, garage.id));
    } else {
      dispatch(addGarage(name.value, buildingId, entranceIds));
    }
  };

  const onDelete = () => {
    dispatch(openModal('DeleteModal', {item: garage, type: 'garage'}));
  };

  const nameError = () => {
    if (typeof garagesError === 'string' && garagesError !== '') {
      return garagesError;
    }
    return fieldError(garagesError, 'name');
  };

  return (
    <>
      <Typography alignSelf="center" variant="h6" mt="1rem" fontWeight="bold">
        {edit ? 'Edit garage' : 'Add a new garage'}
      </Typography>
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexDirection="column"
          mt="2rem"
          gap="1rem"
          sx={{
            width: {xs: 'calc(100vw - 4rem)', sm: '30rem'},
          }}>
          <TextField
            {...name}
            error={isFieldIvalid(garagesError, 'name')}
            helperText={nameError()}
            required
            label="Garage name"
          />
          <Select
            multiple
            items={entrances}
            value={entranceIds}
            onChangeValue={setEntranceIds}
            label="Entrances (optional)"
            disabled={entrancesLoading || entrances.length === 0}
            error={isFieldIvalid(garagesError, 'entranceIds')}
            helperText={
              fieldError(garagesError, 'entranceIds') ||
              (entrances.length === 0 && !entrancesLoading
                ? 'This project has no entrances yet'
                : '')
            }
          />
          <CustomButton type="submit" loading={garagesLoading}>
            {edit ? 'Save' : 'Submit'}
          </CustomButton>
          {edit && (
            <CustomButton
              color="quaternary"
              variant="outlined"
              onClick={onDelete}
              disabled={garagesLoading}>
              Delete garage
            </CustomButton>
          )}
        </Box>
      </form>
    </>
  );
};

export default GarageModal;
